
import React from 'react';
import { Target, Eye, Heart, ShieldCheck, TrendingUp, Award } from 'lucide-react';

const About: React.FC = () => {
  return (
    <div className="bg-white min-h-screen">
      <section className="bg-slate-50 py-20 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-6">About EduZone.pk</h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Empowering the next generation of Pakistani leaders through high-quality, free educational resources tailored for the All Board and Punjab curriculum.
          </p>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-8 leading-tight">Who We Are</h2>
              <div className="space-y-6 text-slate-600 leading-relaxed text-lg">
                <p>
                  EduZone is a premier digital educational initiative based in the heart of Lahore, Pakistan. We recognized the challenges faced by thousands of students in accessing quality study material without heavy financial burdens. 
                </p>
                <p>
                  Our team consists of academic experts, former board examiners, and dedicated educators who believe that education should be accessible to all, regardless of their background. We specialize in providing notes that are specifically aligned with the BISE Lahore standards.
                </p>
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1711820891459-a424674561ef?q=80&w=435&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" 
                alt="Academic Excellence" 
                className="w-full h-auto" 
                referrerPolicy="no-referrer"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="bg-white p-10 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition">
              <div className="bg-blue-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-8">
                <Target className="text-blue-600 w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h3>
              <p className="text-slate-600 leading-relaxed">
                To provide comprehensive, accurate, and easy-to-understand study resources to every student across Punjab, enabling them to achieve academic excellence.
              </p>
            </div>
            <div className="bg-white p-10 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition">
              <div className="bg-green-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-8">
                <Eye className="text-green-600 w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Vision</h3>
              <p className="text-slate-600 leading-relaxed">
                To become the primary authority for education in Pakistan, where every learner has the digital tools to succeed and contribute to the nation's progress.
              </p>
            </div>
            <div className="bg-white p-10 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl transition">
              <div className="bg-red-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-8">
                <Heart className="text-red-600 w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Values</h3>
              <p className="text-slate-600 leading-relaxed">
                Integrity, Accessibility, Quality, and Student-First mindset drive every piece of content we publish on the EduZone portal.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-24 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-6">Commitment to Free Education</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
              We believe that financial constraints should never stand in the way of a student's potential. EduZone remains committed to keeping our core resources free forever.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-500 mb-2">500+</div>
              <p className="text-slate-400 uppercase tracking-widest text-sm font-bold">Sample Chapters</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-500 mb-2">4</div>
              <p className="text-slate-400 uppercase tracking-widest text-sm font-bold">Academic Levels</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-500 mb-2">Lahore</div>
              <p className="text-slate-400 uppercase tracking-widest text-sm font-bold">Headquarters</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-500 mb-2">FREE</div>
              <p className="text-slate-400 uppercase tracking-widest text-sm font-bold">Lifetime Access</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-50 p-12 md:p-16 rounded-3xl border border-blue-100">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold text-slate-900 mb-8 leading-tight">Future Expansion Plans</h2>
              <div className="space-y-6 text-slate-700 leading-relaxed text-lg">
                <p>
                  While our current focus is the All Board (All Board), EduZone is rapidly expanding its horizon. Our roadmap for the next 18 months includes:
                </p>
                <ul className="space-y-4">
                  <li className="flex items-center space-x-3">
                    <TrendingUp className="text-blue-600 flex-shrink-0" />
                    <span>Expansion to Sindh, KPK, and Balochistan Board syllabi.</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <TrendingUp className="text-blue-600 flex-shrink-0" />
                    <span>Launch of an interactive AI-tutor for real-time question solving.</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <TrendingUp className="text-blue-600 flex-shrink-0" />
                    <span>Video lecture series for conceptual subjects like Physics and Math.</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <TrendingUp className="text-blue-600 flex-shrink-0" />
                    <span>Live webinar sessions with top-position holders of All Boards.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
